import { HardHat, Construction, Folder } from "lucide-react";

import React, {

  useCallback,

  useEffect,

  useMemo,

  useState,

} from "react";

import { useParams } from "react-router-dom";

import {
  ContractTreeAPI,
  ContractTreeNode,
  RateGroupAPI,
  RateItemAPI,
  RateWorkGroupAPI,
 RateGroupDeleteAPI,
  ExistingRateGroupActivity,
  TenderTreeAPI,
  UnitOption,
} from "../Services/Api";

type ContractSectionTabsProps = {

  refreshKey?: number;

  // null = main section

  // number = subsection under that node

  onAddSection: (

    parentNode: number | null

  ) => void;

  // Add item for a specific section

  onAddItem?: (

    node: ContractTreeNode

  ) => void;

  // Add a Sub Item under a specific Trade Item

  onAddSubItem?: (

    rateGroupNo: number,
   wbsNo: number | null,
   parentRateType: "Trade Item" | "Stage Item"

  ) => void;

  // Add Sub Works under a specific Trade Item

onAddSubWorks?: (
  rateGroupNo: number,
  parentRateType: "Trade Item" | "Stage Item"
) => void;

  selectedSection?: number | null;

  onSelectSection?: (

    node: ContractTreeNode

  ) => void;

};

/*

|--------------------------------------------------------------------------

| Section Number Sorting

|--------------------------------------------------------------------------

*/

const compareSectionNo = (

  a: string | null | undefined,

  b: string | null | undefined

): number => {

  const aValue = (a ?? "").trim();

  const bValue = (b ?? "").trim();

  if (!aValue && !bValue) return 0;

  if (!aValue) return 1;

  if (!bValue) return -1;

  return aValue.localeCompare(

    bValue,

    undefined,

    {

      sensitivity: "base",

      numeric: true,

    }

  );

};

/*

|--------------------------------------------------------------------------

| Flatten Hierarchy

|--------------------------------------------------------------------------

|

| Database relationship:

|

| Main Section:

| parentNode = 0

|

| Subsection:

| parentNode = mainSection.contractNodeNo

|

| Sub-subsection:

| parentNode = subsection.contractNodeNo

|

| The hierarchy is used only to determine display order.

| The UI is displayed as a flat list with visual indentation.

|

|--------------------------------------------------------------------------

*/

type FlatSection = {

  node: ContractTreeNode;

  level: number;

};

const flattenSections = (

  sections: ContractTreeNode[]

): FlatSection[] => {

  const childrenMap = new Map<

    number,

    ContractTreeNode[]

  >();

  /*

  |--------------------------------------------------------------------------

  | Group records by ParentNode

  |--------------------------------------------------------------------------

  */

  sections.forEach((node) => {

    const parentNode =

      node.parentNode ?? 0;

    if (!childrenMap.has(parentNode)) {

      childrenMap.set(

        parentNode,

        []

      );

    }

    childrenMap

      .get(parentNode)!

      .push(node);

  });

  /*

  |--------------------------------------------------------------------------

  | Sort children under every parent

  |--------------------------------------------------------------------------

  */

  childrenMap.forEach((children) => {

    children.sort((a, b) =>

      compareSectionNo(

        a.sectionNo,

        b.sectionNo

      )

    );

  });

  const result: FlatSection[] = [];

  const visited = new Set<number>();

  /*

  |--------------------------------------------------------------------------

  | Recursive traversal

  |--------------------------------------------------------------------------

  */

  const visit = (

    parentNode: number,

    level: number

  ) => {

    const children =

      childrenMap.get(parentNode) ?? [];

    children.forEach((node) => {

      if (

        visited.has(

          node.contractNodeNo

        )

      ) {

        return;

      }

      visited.add(

        node.contractNodeNo

      );

      result.push({

        node,

        level,

      });

      // Add children immediately after their parent

      visit(

        node.contractNodeNo,

        level + 1

      );

    });

  };

  /*

  |--------------------------------------------------------------------------

  | Start from root sections

  |--------------------------------------------------------------------------

  */

  visit(0, 0);

  /*

  |--------------------------------------------------------------------------

  | Add orphan records if any

  |--------------------------------------------------------------------------

  */

  const orphanSections = sections

    .filter(

      (node) =>

        !visited.has(

          node.contractNodeNo

        )

    )

    .sort((a, b) =>

      compareSectionNo(

        a.sectionNo,

        b.sectionNo

      )

    );

  orphanSections.forEach((node) => {

    result.push({

      node,

      level: 0,

    });

  });

  return result;

};

/*

|--------------------------------------------------------------------------

| Component

|--------------------------------------------------------------------------

*/

type PendingTagDelete =
  | { kind: "rateGroup"; rateGroupNo: number; label: string }
  | { kind: "subItem"; rateGroupNo: number; recordNo: number; label: string }
  | { kind: "subWork"; rateGroupNo: number; recordNo: number; label: string }
  | { kind: "spec"; rateGroupNo: number; component: number; selectedItemID: number; label: string };

const ContractSectionTabs: React.FC<

  ContractSectionTabsProps

> = ({

  refreshKey = 0,

  onAddSection,

  onAddItem,

  onAddSubItem,

   onAddSubWorks,

  selectedSection = null,

  onSelectSection,

}) => {

  const { tenderNo } = useParams<{

    tenderNo: string;

  }>();

  const tenderNumber = Number(tenderNo);

  /*

  |--------------------------------------------------------------------------

  | State

  |--------------------------------------------------------------------------

  */

  const [

    sections,

    setSections,

  ] = useState<ContractTreeNode[]>([]);

  // Maximum section depth used only to indent direct items (not displayed).
  const [intentLevel, setIntentLevel] = useState<number>(0);

  const [

    tradeItems,

    setTradeItems,

  ] = useState<ExistingRateGroupActivity[]>([]);

const [

  stageItems,

  setStageItems,

] = useState<ExistingRateGroupActivity[]>([]);

  const [

    loading,

    setLoading,

  ] = useState(false);

  const [

  expandedTradeItems,

  setExpandedTradeItems,

] = useState<Set<number>>(new Set());

const [

  subItems,

  setSubItems,

] = useState<Record<number, any[]>>({});

const [

  subWorks,

  setSubWorks,

] = useState<Record<number, any[]>>({});

const [editingRateGroupNo, setEditingRateGroupNo] =
  useState<number | null>(null);

const [editingRateField, setEditingRateField] =
  useState<"name" | "rate" | null>(null);

const [editRateGroupName, setEditRateGroupName] =
  useState("");

const [editRate, setEditRate] = useState("");

const [savingRateGroup, setSavingRateGroup] =
  useState(false);

// Units are loaded for each direct Trade Item. Selected unit IDs are persisted in RateGroup.Unit.
const [unitsByRateGroup, setUnitsByRateGroup] = useState<Record<number, UnitOption[]>>({});
const [loadingUnitsByRateGroup, setLoadingUnitsByRateGroup] = useState<Record<number, boolean>>({});
const [selectedUnitByRateGroup, setSelectedUnitByRateGroup] = useState<Record<number, string>>({});

type AvailableSpec = {
  id: number;
  itemId: number;
  name: string;
  type: "Activity" | "ComponentGroup";
  activityNo: number | null;
  componentGroupNo: number | null;
  // Preserve the signed Activity/Component Group identifier for the API.
  component: number;
};

const [specsByRateGroup, setSpecsByRateGroup] = useState<
  Record<number, AvailableSpec[]>
>({});
const [loadingSpecs, setLoadingSpecs] = useState<Record<number, boolean>>({});

// A tag is only marked for deletion after the user clicks its × button.
const [pendingTagDelete, setPendingTagDelete] = useState<PendingTagDelete | null>(null);
const [deletingTag, setDeletingTag] = useState(false);

// Contract sections use the same confirmation popup as tag deletion.
const [pendingSectionDelete, setPendingSectionDelete] =
  useState<ContractTreeNode | null>(null);
const [deletingSection, setDeletingSection] = useState(false);

// Specs popup: options loaded for the Trade Item currently being edited.
const [showSpecsPopup, setShowSpecsPopup] = useState(false);
const [activeSpecsRateGroupNo, setActiveSpecsRateGroupNo] = useState<number | null>(null);
const [availableSpecs, setAvailableSpecs] = useState<AvailableSpec[]>([]);
const [selectedSpec, setSelectedSpec] = useState("");

const [relatedItems, setRelatedItems] = useState<
  { id: number; name: string }[]
>([]);
const [selectedRelatedId, setSelectedRelatedId] = useState("");
const [loadingRelated, setLoadingRelated] = useState(false);


  /*

  |--------------------------------------------------------------------------

  | Load Sections + Trade Items

  |--------------------------------------------------------------------------

  */

  const loadSections = useCallback(

    async () => {

      if (

        !Number.isInteger(

          tenderNumber

        ) ||

        tenderNumber <= 0

      ) {

        setSections([]);

        setTradeItems([]);
        setStageItems([]);

        return;

      }

      try {

        setLoading(true);

        /*

        |--------------------------------------------------------------------------

        | Load both Contract Sections and RateGroup TradeItems

        |--------------------------------------------------------------------------

        */

        const [

          sectionData,

          itemData,

        ] = await Promise.all([

          ContractTreeAPI.getByContract(

            tenderNumber

          ),

          RateGroupAPI.getAddedActivities(

            tenderNumber

          ),

        ]);

        /*

        |--------------------------------------------------------------------------

        | Contract Sections

        |--------------------------------------------------------------------------

        */

        const sectionResult =

          Array.isArray(sectionData)

            ? sectionData

            : [];

        console.log(

          "Contract Sections:",

          sectionResult

        );

        setSections(

          sectionResult

        );

        /*

        |--------------------------------------------------------------------------

        | Trade Items

        |--------------------------------------------------------------------------

        |

        | Only display Stage = 0 Trade Items.

        |

        | We intentionally DO NOT remove duplicates.

        |

        */

        const itemResult = Array.isArray(itemData)
          ? itemData
          : [];

        const tradeItemResult = itemResult.filter(
          (item) =>
            Number(item.stage) === 0 &&
            item.rateType === "Trade Item"
        );

        const stageItemResult = itemResult.filter(
          (item) => item.rateType === "Stage Item"
        );

        console.log("Trade Items:", tradeItemResult);
        console.log("Stage Items:", stageItemResult);

        setTradeItems(tradeItemResult);
        setStageItems(stageItemResult);

        // Restore each saved unit from RateGroup.Unit when the data is loaded.
        setSelectedUnitByRateGroup(
          tradeItemResult.reduce<Record<number, string>>((result, item) => {
            result[item.rateGroupNo] =
              item.unit == null ? "" : String(item.unit);
            return result;
          }, {})
        );

      } catch (error) {

        console.error(

          "Unable to load Contract Sections / Trade Items:",

          error

        );

        setSections([]);

        setTradeItems([]);
        setStageItems([]);

      } finally {

        setLoading(false);

      }

    },

    [tenderNumber]

  );

  // Load the specification selections persisted for this RateGroup.
  const loadSavedSpecs = async (rateGroupNo: number) => {
    try {
      const savedRows =
        await TenderTreeAPI.getSavedRateGroupSpecs(rateGroupNo);
      const rows = Array.isArray(savedRows) ? savedRows : [];

      if (rows.length === 0) {
        setSpecsByRateGroup((previous) => ({
          ...previous,
          [rateGroupNo]: [],
        }));
        return;
      }

      const resolvedSpecs = await Promise.all(
        rows.map(async (row) => {
          const component = Number(row.component);
          const relatedItems =
            await TenderTreeAPI.getSpecRelatedItems(component);
          const selectedItem = relatedItems.find(
            (relatedItem) =>
              Number(relatedItem.id) === Number(row.selectedItemID)
          );

          return { row, selectedItem };
        })
      );

      const savedSpecs: AvailableSpec[] = resolvedSpecs
        .filter(({ selectedItem }) => Boolean(selectedItem))
        .map(({ row, selectedItem }) => {
          const component = Number(row.component);
          const type: AvailableSpec["type"] =
            component < 0 ? "Activity" : "ComponentGroup";
          const itemId = Math.abs(Number(row.selectedItemID));

          return {
            id: Number(row.selectedItemID),
            itemId,
            name: selectedItem!.name,
            type,
            activityNo:
              type === "Activity" ? Math.abs(component) : null,
            componentGroupNo:
              type === "ComponentGroup" ? Math.abs(component) : null,
            component,
          };
        });

      setSpecsByRateGroup((previous) => ({
        ...previous,
        [rateGroupNo]: savedSpecs,
      }));
    } catch (error) {
      console.error(
        `Unable to load saved specifications for RateGroup ${rateGroupNo}:`,
        error
      );
    }
  };

  const handleAddSpecs = async (item: ExistingRateGroupActivity) => {
    const rateGroupNo = Number(item.rateGroupNo);

    if (!Number.isInteger(rateGroupNo) || rateGroupNo <= 0) {
      alert("Invalid Rate Group No.");
      return;
    }

    setLoadingSpecs((previous) => ({
      ...previous,
      [rateGroupNo]: true,
    }));

    try {
      // Load specifications using RateGroupNo, not ActivityNo.
      const specs = await TenderTreeAPI.getSpecsByRateGroup(rateGroupNo);
      const rawSpecs = Array.isArray(specs) ? specs : [];

      // Negative IDs represent Activity rows; positive IDs represent
      // Component Group rows. Preserve the original signed ID.
      const specOptions: AvailableSpec[] = rawSpecs.map((spec: any) => {
        const id = Number(spec.id ?? spec.componentGroupNo ?? 0);
        // Some API responses include `component`; fall back to the signed ID.
        const component = Number(spec.component ?? spec.Component ?? id);
        const type: AvailableSpec["type"] =
          component < 0 ? "Activity" : "ComponentGroup";
        const itemId = Math.abs(id);

        return {
          id,
          itemId,
          name: String(spec.name ?? spec.componentGroup ?? ""),
          type,
          activityNo: type === "Activity" ? itemId : null,
          componentGroupNo:
            type === "ComponentGroup" ? itemId : null,
          component,
        };
      });

      setAvailableSpecs(specOptions);
      setActiveSpecsRateGroupNo(rateGroupNo);
      setSelectedSpec("");
      setSelectedRelatedId("");
      setRelatedItems([]);
      setShowSpecsPopup(true);
    } catch (error: any) {
      console.error("Failed to load specifications:", error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load specifications."
      );
    } finally {
      setLoadingSpecs((previous) => ({
        ...previous,
        [rateGroupNo]: false,
      }));
    }
  };

  const handleSpecChange = async (value: string) => {
    setSelectedSpec(value);
    setSelectedRelatedId("");
    setRelatedItems([]);

    if (value === "") return;

    const selected = availableSpecs[Number(value)];
    if (!selected) return;

    try {
      setLoadingRelated(true);
      const items = await TenderTreeAPI.getSpecRelatedItems(selected.component);
      setRelatedItems(
        Array.isArray(items)
          ? items.filter(
              (item) =>
                Number.isFinite(Number(item.id)) &&
                String(item.name ?? "").trim() !== ""
            )
          : []
      );
    } catch (error) {
      console.error("Failed to load related specification items:", error);
      setRelatedItems([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  const handleConfirmSpec = async () => {
    if (
      activeSpecsRateGroupNo === null ||
      selectedSpec === "" ||
      selectedRelatedId === ""
    ) {
      return;
    }

    const rateGroupNo = activeSpecsRateGroupNo;
    const selectedGroup = availableSpecs[Number(selectedSpec)];
    const selectedItem = relatedItems.find(
      (item) => String(item.id) === selectedRelatedId
    );

    if (!selectedGroup || !selectedItem) {
      alert("Please select a specification group and item.");
      return;
    }

    try {
      setLoadingSpecs((previous) => ({
        ...previous,
        [rateGroupNo]: true,
      }));

      // Persist the selected group and selected child item in RateGroupSpecs.
      await TenderTreeAPI.addRateGroupSpec({
        rateGroupNo,
        component: selectedGroup.component,
        selectedItemID: selectedItem.id,
      });

      // Read the database again so the UI reflects the saved records.
      await loadSavedSpecs(rateGroupNo);

      setShowSpecsPopup(false);
      setActiveSpecsRateGroupNo(null);
      setSelectedSpec("");
      setSelectedRelatedId("");
      setRelatedItems([]);
    } catch (error: any) {
      console.error("Unable to save specification:", error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save the specification."
      );
    } finally {
      setLoadingSpecs((previous) => ({
        ...previous,
        [rateGroupNo]: false,
      }));
    }
  };

  const handleSaveRateGroup = async (
    item: ExistingRateGroupActivity
  ) => {
    const name = editRateGroupName.trim();
    const rate = Number(editRate);

    if (!name) {
      alert("Rate Group name is required.");
      return;
    }

    if (editRate.trim() === "" || !Number.isFinite(rate) || rate < 0) {
      alert("Enter a valid non-negative rate.");
      return;
    }

    try {
      setSavingRateGroup(true);

      await RateGroupAPI.updateRateGroup({
        rateGroupNo: item.rateGroupNo,
        rateGroup: name,
        rate,
      });

      setEditingRateGroupNo(null);
      setEditingRateField(null);
      await loadSections();
    } catch (error: any) {
      console.error("Unable to update Rate Group:", error);
      alert(
        error?.response?.data?.message ||
          "Unable to update Rate Group."
      );
    } finally {
      setSavingRateGroup(false);
    }
  };

  const cancelRateGroupEdit = () => {
    setEditingRateGroupNo(null);
    setEditingRateField(null);
    setEditRateGroupName("");
    setEditRate("");
  };

  /*
  |--------------------------------------------------------------------------
  | Refresh

  |--------------------------------------------------------------------------

  |

  | This runs:

  |

  | 1. On first load

  | 2. When a section is added

  | 3. When a TradeItem is added

  |

  |--------------------------------------------------------------------------

  */

  useEffect(() => {

    loadSections();

  }, [

    loadSections,

    refreshKey,

  ]);


  /*
   |--------------------------------------------------------------------------
   | Load Maximum Section Level
   |--------------------------------------------------------------------------
   | Used for indentation of direct Trade Items and Stage Items only.
   | The numeric level is not displayed in the UI.
   |--------------------------------------------------------------------------
  */
  useEffect(() => {
    let cancelled = false;

    const loadMaximumSectionLevel = async () => {
      if (!Number.isInteger(tenderNumber) || tenderNumber <= 0) {
        setIntentLevel(0);
        return;
      }

      try {
        const maximumLevel = await ContractTreeAPI.getMaxLevel(tenderNumber);
        if (!cancelled) {
          const parsedLevel = Number(maximumLevel);
          setIntentLevel(Number.isFinite(parsedLevel) ? Math.max(0, parsedLevel) : 0);
        }
      } catch (error) {
        console.error("Unable to load maximum section level:", error);
        if (!cancelled) setIntentLevel(0);
      }
    };

    void loadMaximumSectionLevel();
    return () => { cancelled = true; };
  }, [tenderNumber, refreshKey]);

  /*

  |--------------------------------------------------------------------------

  | Flat Display List

  |--------------------------------------------------------------------------

  */

  const flatSections = useMemo(() => {

    return flattenSections(

      sections

    );

  }, [sections]);

  /*

  |--------------------------------------------------------------------------

  | Direct Trade Items

  |--------------------------------------------------------------------------

  |

  | ContractNode = 0 means the item was added directly

  | from the left-side Add Item menu.

  |

  |--------------------------------------------------------------------------

  */

  const directTradeItems = useMemo(() => {

    return tradeItems.filter(

      (item) =>

        Number(item.contractNode) === 0

    );

  }, [tradeItems]);

  // Load available units for every Trade Item (direct, section-level, and nested) using its activity number (Rider).
  useEffect(() => {
    let cancelled = false;

    const loadUnits = async () => {
      await Promise.all(
        tradeItems.map(async (item) => {
          const activityNo = Number(item.rider);
          if (!Number.isInteger(activityNo) || activityNo <= 0) {
            if (!cancelled) {
              setUnitsByRateGroup((previous) => ({ ...previous, [item.rateGroupNo]: [] }));
            }
            return;
          }

          if (!cancelled) {
            setLoadingUnitsByRateGroup((previous) => ({ ...previous, [item.rateGroupNo]: true }));
          }

          try {
            const units = await TenderTreeAPI.getUnitsByActivity(activityNo);
            if (!cancelled) {
              setUnitsByRateGroup((previous) => ({
                ...previous,
                [item.rateGroupNo]: Array.isArray(units) ? units : [],
              }));
            }
          } catch (error) {
            console.error(`Unable to load units for activity ${activityNo}:`, error);
            if (!cancelled) {
              setUnitsByRateGroup((previous) => ({ ...previous, [item.rateGroupNo]: [] }));
            }
          } finally {
            if (!cancelled) {
              setLoadingUnitsByRateGroup((previous) => ({ ...previous, [item.rateGroupNo]: false }));
            }
          }
        })
      );
    };

    void loadUnits();
    return () => { cancelled = true; };
  }, [tradeItems]);

  const directStageItems = useMemo(() => {
    return stageItems.filter(
      (item) => Number(item.contractNode) === 0
    );
  }, [stageItems]);

  /*

  |--------------------------------------------------------------------------

  | Get Trade Items for Section

  |--------------------------------------------------------------------------

  */

  const getTradeItemsForSection = (

    contractNodeNo: number

  ) => {

    return tradeItems.filter(

      (item) =>

        Number(item.contractNode) ===

        Number(contractNodeNo)

    );

  };

  const getStageItemsForSection = (
    contractNodeNo: number
  ) => {
    return stageItems.filter(
      (item) =>
        Number(item.contractNode) === Number(contractNodeNo)
    );
  };

const loadSubItems = async (

  rateGroupNo: number

) => {

  try {

    const result =

      await RateItemAPI.getByRateGroup(

        rateGroupNo

      );

    setSubItems((previous) => ({

      ...previous,

      [rateGroupNo]: result || [],

    }));

  } catch (error) {

    console.error(

      "Unable to load Sub Items:",

      error

    );

    setSubItems((previous) => ({

      ...previous,

      [rateGroupNo]: [],

    }));

  }

};

const loadSubWorks = async (

  rateGroupNo: number

) => {

  try {

    const result =

      await RateWorkGroupAPI.getByRateGroup(

        rateGroupNo

      );

    setSubWorks((previous) => ({

      ...previous,

      [rateGroupNo]: result || [],

    }));

  } catch (error) {

    console.error(

      "Unable to load Sub Works:",

      error

    );

    setSubWorks((previous) => ({

      ...previous,

      [rateGroupNo]: [],

    }));

  }

};


const confirmTagDelete = async () => {
  if (!pendingTagDelete || deletingTag) return;

  const target = pendingTagDelete;
  setDeletingTag(true);

  try {
    if (target.kind === "rateGroup") {
      // Soft-delete the parent RateGroup and its related child records through the API.
      await RateGroupDeleteAPI.deleteRateGroup(target.rateGroupNo);

      setTradeItems((previous) =>
        previous.filter((item) => Number(item.rateGroupNo) !== target.rateGroupNo)
      );
      setStageItems((previous) =>
        previous.filter((item) => Number(item.rateGroupNo) !== target.rateGroupNo)
      );

      setSubItems((previous) => {
        const updated = { ...previous };
        delete updated[target.rateGroupNo];
        return updated;
      });
      setSubWorks((previous) => {
        const updated = { ...previous };
        delete updated[target.rateGroupNo];
        return updated;
      });
      setSpecsByRateGroup((previous) => {
        const updated = { ...previous };
        delete updated[target.rateGroupNo];
        return updated;
      });
      setUnitsByRateGroup((previous) => {
        const updated = { ...previous };
        delete updated[target.rateGroupNo];
        return updated;
      });
      setLoadingUnitsByRateGroup((previous) => {
        const updated = { ...previous };
        delete updated[target.rateGroupNo];
        return updated;
      });
      setSelectedUnitByRateGroup((previous) => {
        const updated = { ...previous };
        delete updated[target.rateGroupNo];
        return updated;
      });
      setLoadingSpecs((previous) => {
        const updated = { ...previous };
        delete updated[target.rateGroupNo];
        return updated;
      });
      setExpandedTradeItems((previous) => {
        const updated = new Set(previous);
        updated.delete(target.rateGroupNo);
        return updated;
      });

      if (editingRateGroupNo === target.rateGroupNo) {
        cancelRateGroupEdit();
      }
    } else if (target.kind === "subItem") {
      await RateGroupDeleteAPI.deleteRateItem(target.recordNo);
      setSubItems((previous) => ({
        ...previous,
        [target.rateGroupNo]: (previous[target.rateGroupNo] || []).filter(
          (row) => Number(row.rateItemNo) !== target.recordNo
        ),
      }));
    } else if (target.kind === "subWork") {
      await RateGroupDeleteAPI.deleteRateWorkGroup(target.recordNo);
      setSubWorks((previous) => ({
        ...previous,
        [target.rateGroupNo]: (previous[target.rateGroupNo] || []).filter(
          (row) => Number(row.rateWorkGroupNo) !== target.recordNo
        ),
      }));
    } else {
      await RateGroupDeleteAPI.deleteRateGroupSpec(
        target.rateGroupNo,
        target.component,
        target.selectedItemID
      );
      setSpecsByRateGroup((previous) => ({
        ...previous,
        [target.rateGroupNo]: (previous[target.rateGroupNo] || []).filter(
          (spec) => !(
            Number(spec.component) === target.component &&
            Number(spec.id) === target.selectedItemID
          )
        ),
      }));
    }

    setPendingTagDelete(null);
  } catch (error: any) {
    console.error("Unable to delete tag:", error);
    alert(
      error?.response?.data?.message ||
      error?.message ||
      "Unable to delete. Please try again."
    );
  } finally {
    setDeletingTag(false);
  }
};


const confirmSectionDelete = async () => {
  if (!pendingSectionDelete || deletingSection) return;

  const contractNodeNo = Number(pendingSectionDelete.contractNodeNo);
  if (!Number.isInteger(contractNodeNo) || contractNodeNo <= 0) {
    alert("Invalid Contract Section.");
    return;
  }

  try {
    setDeletingSection(true);
    await ContractTreeAPI.deleteSection(contractNodeNo);
    setPendingSectionDelete(null);
    await loadSections();
  } catch (error: any) {
    console.error("Unable to delete Contract Section:", error);
    alert(
      error?.response?.data?.message ||
        error?.message ||
        "Unable to delete Contract Section. Please try again."
    );
  } finally {
    setDeletingSection(false);
  }
};

const toggleTradeItem = async (

  rateGroupNo: number

) => {

  const isCurrentlyExpanded =

    expandedTradeItems.has(rateGroupNo);

  // Collapse

  if (isCurrentlyExpanded) {

    setExpandedTradeItems((previous) => {

      const next = new Set(previous);

      next.delete(rateGroupNo);

      return next;

    });

    return;

  }

  // Expand

  setExpandedTradeItems((previous) => {

    const next = new Set(previous);

    next.add(rateGroupNo);

    return next;

  });

 await Promise.all([

  loadSubItems(rateGroupNo),

  loadSubWorks(rateGroupNo),

  loadSavedSpecs(rateGroupNo),

]);

};

useEffect(() => {

  const refreshExpandedTradeItems = async () => {

    const expandedItems =

      Array.from(expandedTradeItems);

    if (expandedItems.length === 0) {

      return;

    }

   for (const rateGroupNo of expandedItems) {

  await Promise.all([

    loadSubItems(rateGroupNo),

    loadSubWorks(rateGroupNo),

    loadSavedSpecs(rateGroupNo),

  ]);

}

  };

  refreshExpandedTradeItems();

  // refreshKey is intentionally the trigger.

  // eslint-disable-next-line react-hooks/exhaustive-deps

}, [refreshKey]);

  /*

  |--------------------------------------------------------------------------

  | Select Section

  |--------------------------------------------------------------------------

  */

  const selectSection = (

    node: ContractTreeNode

  ) => {

    onSelectSection?.(node);

  };

  /*

  |--------------------------------------------------------------------------

  | Add Item

  |--------------------------------------------------------------------------

  |

  | The current row becomes the selected section.

  | The Add Item popup from the parent component

  | will then be opened.

  |

  |--------------------------------------------------------------------------

  */

  const addItem = (

    node: ContractTreeNode

  ) => {

    // Select this section

    onSelectSection?.(node);

    // Open Add Item popup

    onAddItem?.(node);

  };

  /*

  |--------------------------------------------------------------------------

  | Add Subsection

  |--------------------------------------------------------------------------

  |

  | Sends the current section's ContractNodeNo

  | to the parent component.

  |

  |--------------------------------------------------------------------------

  */

  const addSubsection = (

    node: ContractTreeNode

  ) => {

    onAddSection(

      node.contractNodeNo

    );

  };

  /*

  |--------------------------------------------------------------------------

  | Render

  |--------------------------------------------------------------------------

  */

  return (

    <div
      className="flex h-full w-full flex-col overflow-hidden bg-[#f8f9fa]"
      style={{
        fontFamily: '"Segoe UI", sans-serif',
        fontSize: "13px",
        fontWeight: 400,
      }}
    >

      {/* =================================================

          CONTENT

      ================================================= */}

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">

        {/* =================================================

            LOADING

        ================================================= */}

        {loading && (

          <div className="px-3 py-5 text-center text-[13px] text-gray-500">

            Loading Contract Sections...

          </div>

        )}

        {/* =================================================

            EMPTY

        ================================================= */}

        {!loading &&

          flatSections.length === 0 &&

          directTradeItems.length === 0 &&
          directStageItems.length === 0 && (

            <div className="flex h-full flex-col items-center justify-center px-4 text-center">

              <button

                type="button"

                onClick={() =>

                  onAddSection(null)

                }

                className="mb-3 flex h-[44px] w-[44px] items-center justify-center rounded-full border border-gray-200 bg-white text-[13px] text-[#5f6368] transition hover:border-[#9bbcf0] hover:bg-[#eef4ff] hover:text-[#53538c]"

                title="Add Section"

              >

                +

              </button>

              <div className="text-[13px] font-normal text-[#3c4043]">

                No sections created

              </div>

              <div className="mt-1 text-[13px] text-[#9aa0a6]">

                Click + to create the first section.

              </div>

            </div>

          )}

        {/* =================================================

            FLAT SECTION LIST

        ================================================= */}

        {!loading &&

          flatSections.length > 0 && (

            <div className="flex flex-col gap-[2px]">

              {flatSections.map(

                ({

                  node,

                  level,

                }) => {

                  const isSelected =

                    selectedSection ===

                    node.contractNodeNo;

                  const isMainSection =

                    level === 0;

                  /*

                  |--------------------------------------------------------------------------

                  | Trade Items belonging to this section

                  |--------------------------------------------------------------------------

                  */

                  const sectionTradeItems =

                    getTradeItemsForSection(

                      node.contractNodeNo

                    );

                  const sectionStageItems =
                    getStageItemsForSection(node.contractNodeNo);

                  return (

                    <React.Fragment

                      key={

                        node.contractNodeNo

                      }

                    >

                      {/* =================================================

                          SECTION ROW

                      ================================================= */}

                      <div

                        className={`group flex min-h-[42px] w-full items-center rounded-lg border transition ${

                          isSelected

                            ? "border-[#9bbcf0] bg-[#d3e3fd]"

                            : "border-transparent bg-white hover:border-gray-200 hover:bg-[#f1f3f4]"

                        }`}

                        style={{

                          paddingLeft: `${level * 16}px`,

                        }}

                      >

                        {/* =================================================

                            SECTION CLICK AREA

                        ================================================= */}

                        <button

                          type="button"

                          onClick={() =>

                            selectSection(

                              node

                            )

                          }

                          title={

                            node.contractNode

                          }

                          className="flex min-w-0 items-center gap-1.5 overflow-hidden px-2 text-left"

                        >

                          {/* SECTION ICON */}

                          <span

                            className={`flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded text-[13px] ${

                              isSelected

                                ? "bg-[#c2d8f8] text-[#1a73e8]"

                                : isMainSection

                                  ? "text-[#53538c]"

                                  : "text-[#6d96f2]"

                            }`}

                          >

                            <Folder size={15} strokeWidth={1.8} className="shrink-0" />

                          </span>

                          {/* SECTION NUMBER */}

                          <span

                            className={`shrink-0 text-[13px] font-normal ${

                              isSelected

                                ? "text-[#174ea6]"

                                : isMainSection

                                  ? "text-[#53538c]"

                                  : "text-[#6d96f2]"

                            }`}

                          >

                            {node.sectionNo ||

                              "-"}

                          </span>

                          {/* SECTION NAME */}

                          <span

                            className={`whitespace-nowrap text-[13px] ${

                              isSelected

                                ? "font-normal text-[#174ea6]"

                                : isMainSection

                                  ? "font-normal text-[#202124]"

                                  : "text-[#5f6368]"

                            }`}

                          >

                            {node.contractNode}

                          </span>



                        </button>

                        {/* =================================================

                            ADD SECTION

                            Always visible immediately beside section name

                        ================================================= */}

                        <button

                          type="button"

                          title={`Add section under ${node.contractNode}`}

                          onClick={(event) => {

                            event.stopPropagation();

                            addSubsection(

                              node

                            );

                          }}

                          className="ml-1 flex h-[24px] shrink-0 items-center gap-1 rounded-md px-2 text-[13px] text-[#6d96f2] transition hover:bg-[#e8f0fe] hover:text-[#1a73e8]"

                        >

                          <span className="text-[13px] leading-none">

                            +

                          </span>

                          <span>

                            Section

                          </span>

                        </button>

                        {/* =================================================

                            ADD ITEM

                            Always visible immediately after Section

                        ================================================= */}

                        <button

                          type="button"

                          title={`Add item under ${node.contractNode}`}

                          onClick={(event) => {

                            event.stopPropagation();

                            addItem(

                              node

                            );

                          }}

                          className="flex h-[24px] shrink-0 items-center gap-1 rounded-md px-2 text-[13px] text-[#5f6368] transition hover:bg-[#c2e7ff] hover:text-[#1a73e8]"

                        >

                          <span className="text-[13px] leading-none">

                            +

                          </span>

                          <span>

                            Item

                          </span>

                        </button>

                        {/* DELETE SECTION - VISIBLE ON HOVER */}
                        <button
                          type="button"
                          title={`Delete section ${node.contractNode}`}
                          aria-label={`Delete section ${node.contractNode}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setPendingSectionDelete(node);
                          }}
                          className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                        >
                          ×
                        </button>

                      </div>

                      {/* =================================================

                          TRADE ITEMS UNDER THIS SECTION

                      ================================================= */}

                      {sectionTradeItems.length > 0 && (

  <div

    className="flex flex-col gap-[2px]"

    style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}

  >

    {sectionTradeItems.map((item) => (

      <React.Fragment key={item.rateGroupNo}>

        <div className="group relative flex min-h-[36px] w-full items-center rounded-md border border-transparent bg-white px-3 hover:border-gray-200 hover:bg-[#f8f9fa]">

          <button

            type="button"

            onClick={(event) => {

              event.stopPropagation();

              toggleTradeItem(item.rateGroupNo);

            }}

            className="mr-1 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded hover:bg-gray-100"

            title={expandedTradeItems.has(item.rateGroupNo) ? "Collapse Sub Items" : "Expand Sub Items"}

          >

            <span className="text-[13px] text-gray-500">

              {expandedTradeItems.has(item.rateGroupNo) ? "▼" : "▶"}

            </span>

          </button>

          <span className="mr-2 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-[#e8f0fe] text-[#2563eb]" title="Trade Item">
            <HardHat size={15} strokeWidth={1.8} />
          </span>

          {editingRateGroupNo === item.rateGroupNo && editingRateField === "name" ? (
        <input
          autoFocus
          value={editRateGroupName}
          onChange={(event) => setEditRateGroupName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="w-fit min-w-[4ch] max-w-[260px] rounded border border-gray-300 px-1.5 py-1 text-[13px]"
          style={{ width: `${Math.min(32, Math.max(4, editRateGroupName.length + 1))}ch` }}
          aria-label="Rate Group name"
        />
      ) : (
        <button
          type="button"
          title="Click to edit name"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("name");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="inline-block w-fit max-w-[260px] truncate text-left text-[13px] font-normal text-[#202124] no-underline"
        >{item.rateGroup || "-"}</button>
      )}

          <button
            type="button"
            title="Delete Rate Group"
            aria-label={`Delete ${item.rateGroup || "Rate Group"}`}
            onClick={(event) => {
              event.stopPropagation();
              setPendingTagDelete({
                kind: "rateGroup",
                rateGroupNo: Number(item.rateGroupNo),
                label: item.rateGroup || "Rate Group",
              });
            }}
            className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
          >
            ×
          </button>

          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-3">
      {editingRateGroupNo === item.rateGroupNo && editingRateField === "rate" ? (
        <input
          autoFocus
          type="number"
          min="0"
          step="0.01"
          value={editRate}
          onChange={(event) => setEditRate(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="h-[27px] w-24 shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-center text-[13px]"
          aria-label="Rate"
        />
      ) : (
        <button
          type="button"
          title="Click to edit rate"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("rate");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="shrink-0 whitespace-nowrap text-[13px] font-normal text-[#53538c] no-underline"
        >Rate: {item.rate == null ? "0.00" : Number(item.rate).toFixed(2)}</button>
      )}

      {/* UNIT SELECTOR */}
          <div className="ml-3 flex shrink-0 items-center gap-1.5">
            <label
              htmlFor={`section-trade-item-unit-${item.rateGroupNo}`}
              className="whitespace-nowrap text-[13px] font-normal text-[#5f6368]"
            >
              Unit:
            </label>
            <select
              id={`section-trade-item-unit-${item.rateGroupNo}`}
              value={selectedUnitByRateGroup[item.rateGroupNo] ?? (item.unit == null ? "" : String(item.unit))}
              onChange={async (event) => {
                event.stopPropagation();
                const previousValue = selectedUnitByRateGroup[item.rateGroupNo] ?? (item.unit == null ? "" : String(item.unit));
                const selectedValue = event.target.value;
                const unit = selectedValue === "" ? null : Number(selectedValue);
                if (unit !== null && !Number.isInteger(unit)) return;
                setSelectedUnitByRateGroup((previous) => ({ ...previous, [item.rateGroupNo]: selectedValue }));
                try {
                  await RateGroupAPI.updateRateGroupUnit({ rateGroupNo: item.rateGroupNo, unit });
                } catch (error) {
                  console.error("Unable to save Trade Item unit:", error);
                  setSelectedUnitByRateGroup((previous) => ({ ...previous, [item.rateGroupNo]: previousValue }));
                  alert("Unable to save the selected unit. Please try again.");
                }
              }}
              onClick={(event) => event.stopPropagation()}
              disabled={!!loadingUnitsByRateGroup[item.rateGroupNo] || (unitsByRateGroup[item.rateGroupNo] ?? []).length === 0}
              aria-label={`Unit for ${item.rateGroup || "Trade Item"}`}
              title="Select unit"
              className="h-[27px] w-[115px] shrink-0 rounded border border-gray-200 bg-white px-2 text-[13px] font-normal text-[#3c4043] outline-none focus:border-[#9bbcf0] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">
                {loadingUnitsByRateGroup[item.rateGroupNo]
                  ? "Loading units..."
                  : (unitsByRateGroup[item.rateGroupNo] ?? []).length > 0
                    ? "Select unit"
                    : "No units"}
              </option>
              {(unitsByRateGroup[item.rateGroupNo] ?? []).map((unit) => (
                <option key={unit.unitNo} value={String(unit.unitNo)}>{unit.unitName}</option>
              ))}
            </select>
          </div>

          </div>

      

        </div>

        {/* EXPANDED CARD (Section Trade Items) */}
    {expandedTradeItems.has(item.rateGroupNo) && (() => {
      const filteredSubWorks = (subWorks[item.rateGroupNo] || []).filter(
        (subWork) => Number(subWork.workGroup) !== Number(item.stage)
      );
      const filteredSubItems = (subItems[item.rateGroupNo] || []).filter(
        (subItem) => Number(subItem.procedure) !== Number(item.rider)
      );
      const itemSpecs = specsByRateGroup[item.rateGroupNo] || [];
      const parentRateType = "Trade Item" as const;
      const actionButtonClass = "rounded-md px-2 py-1 text-[13px] text-[#53538c] hover:bg-[#e8f0fe] hover:text-[#1a73e8] disabled:cursor-not-allowed disabled:opacity-60";

      return (
        <div className="ml-[3.5rem] mb-2 mt-1 rounded-md border border-gray-100 bg-[#fafbfc] px-2 py-2">
          {/* Workgroups and Add Sub Works stay on the same row */}
          <div className="flex flex-wrap items-center gap-1.5">
            {filteredSubWorks.map((subWork) => (
              <div key={subWork.rateWorkGroupNo} className="group inline-flex items-center rounded-full border border-[#FDE68A] bg-[#FEFCE8] px-2 py-1 text-[13px] text-[#854D0E]">
                <span className="mr-1" title="Sub work">🛠️</span>
                <span>{subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}</span>
                <button
                  type="button"
                  title="Delete Sub Work"
                  aria-label={`Delete ${subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingTagDelete({
                      kind: "subWork",
                      rateGroupNo: Number(item.rateGroupNo),
                      recordNo: Number(subWork.rateWorkGroupNo),
                      label: subWork.workGroupName || `WorkGroup ${subWork.workGroup}`,
                    });
                  }}
                  className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-[#92400E] opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                >
                  ×
                </button>
              </div>
            ))}
            {filteredSubWorks.length === 0 && (
              <span className="text-[13px] text-gray-500">No workgroups are added.</span>
            )}
            <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubWorks?.(item.rateGroupNo, parentRateType); }} className={actionButtonClass}>
              🛠️ Add Sub Works
            </button>
          </div>

          {/* Sub-items and Add Sub Item stay on the same row */}
          {filteredSubWorks.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {filteredSubItems.map((subItem) => (
                <div key={subItem.rateItemNo} className="group inline-flex items-center rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2 py-1 text-[13px] text-[#1D4ED8]">
                  <span className="mr-1" title="Sub item activity">📋</span>
                  <span>{subItem.activityName || `Activity ${subItem.procedure}`}</span>
                  <button
                    type="button"
                    title="Delete Sub Item"
                    aria-label={`Delete ${subItem.activityName || `Activity ${subItem.procedure}`}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "subItem",
                        rateGroupNo: Number(item.rateGroupNo),
                        recordNo: Number(subItem.rateItemNo),
                        label: subItem.activityName || `Activity ${subItem.procedure}`,
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-blue-700 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {filteredSubItems.length === 0 && (
                <span className="text-[13px] text-gray-500">No sub items are added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubItem?.(item.rateGroupNo, item.wbsNo ?? null, parentRateType); }} className={actionButtonClass}>
                + Add Sub Item
              </button>
            </div>
          )}

          {/* Specifications are shown as items only; no title or empty-state message */}
          {filteredSubWorks.length > 0 && filteredSubItems.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {itemSpecs.map((spec, index) => (
                <div key={`${spec.type}-${spec.id}-${index}`} className="group inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[13px] text-gray-700">
                  <span>{spec.name || "Unnamed specification"}</span>
                  <button
                    type="button"
                    title="Delete Specification"
                    aria-label={`Delete ${spec.name || "Unnamed specification"}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "spec",
                        rateGroupNo: Number(item.rateGroupNo),
                        component: Number(spec.component),
                        selectedItemID: Number(spec.id),
                        label: spec.name || "Unnamed specification",
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-gray-500 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {itemSpecs.length === 0 && (
                <span className="text-[13px] text-gray-500">No specifications added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); void handleAddSpecs(item); }} disabled={!!loadingSpecs[item.rateGroupNo]} className={actionButtonClass}>
                {loadingSpecs[item.rateGroupNo] ? "Loading..." : "+ Add Specs"}
              </button>
            </div>
          )}
        </div>
      );
    })()}

    </React.Fragment>

    ))}

  </div>

)}

                    {/* SECTION STAGE ITEMS */}
                    {sectionStageItems.length > 0 && (
                      <>
                        <div
                          className="flex flex-col gap-[2px]"
                          style={{
                            paddingLeft: `${(level + 1) * 16 + 8}px`,
                          }}
                        >

{sectionStageItems.map((item) => (
  <React.Fragment key={item.rateGroupNo}>
    {/* STAGE ITEM ROW */}
    <div className="group relative flex min-h-[36px] w-full items-center rounded-md border border-transparent bg-white px-3 hover:border-gray-200 hover:bg-[#f8f9fa]">
      {/* Expand / Collapse */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          toggleTradeItem(item.rateGroupNo);
        }}
        className="mr-1 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded hover:bg-gray-100"
        title={
          expandedTradeItems.has(item.rateGroupNo)
            ? "Collapse"
            : "Expand"
        }
      >
        <span className="text-[13px] text-gray-500">
          {expandedTradeItems.has(item.rateGroupNo) ? "▼" : "▶"}
        </span>
      </button>

      {/* Stage Item Icon */}
      <span className="mr-2 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-[#f3e8ff] text-[#7c3aed]" title="Stage Item">
        <Construction size={15} strokeWidth={1.8} />
      </span>

      {/* Stage Item Name */}
      {editingRateGroupNo === item.rateGroupNo && editingRateField === "name" ? (
        <input
          autoFocus
          value={editRateGroupName}
          onChange={(event) => setEditRateGroupName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="w-fit min-w-[4ch] max-w-[260px] rounded border border-gray-300 px-1.5 py-1 text-[13px]"
          style={{ width: `${Math.min(32, Math.max(4, editRateGroupName.length + 1))}ch` }}
          aria-label="Rate Group name"
        />
      ) : (
        <button
          type="button"
          title="Click to edit name"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("name");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="inline-block w-fit max-w-[260px] truncate text-left text-[13px] font-normal text-[#202124] no-underline"
        >{item.rateGroup || "-"}</button>
      )}

      <button
            type="button"
            title="Delete Rate Group"
            aria-label={`Delete ${item.rateGroup || "Rate Group"}`}
            onClick={(event) => {
              event.stopPropagation();
              setPendingTagDelete({
                kind: "rateGroup",
                rateGroupNo: Number(item.rateGroupNo),
                label: item.rateGroup || "Rate Group",
              });
            }}
            className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
          >
            ×
          </button>

          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-3">
      {editingRateGroupNo === item.rateGroupNo && editingRateField === "rate" ? (
        <input
          autoFocus
          type="number"
          min="0"
          step="0.01"
          value={editRate}
          onChange={(event) => setEditRate(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="h-[27px] w-24 shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-center text-[13px]"
          aria-label="Rate"
        />
      ) : (
        <button
          type="button"
          title="Click to edit rate"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("rate");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="shrink-0 whitespace-nowrap text-[13px] font-normal text-[#53538c] no-underline"
        >Rate: {item.rate == null ? "0.00" : Number(item.rate).toFixed(2)}</button>
      )}

      {/* Stage Item unit name */}
      <div className="ml-3 flex shrink-0 items-center gap-1.5" aria-label="Stage Item unit">
        <span className="whitespace-nowrap text-[13px] font-normal text-[#5f6368]">
          Unit:
        </span>
        <span className="inline-flex h-[27px] min-w-[115px] items-center rounded border border-gray-200 bg-gray-50 px-2 text-[13px] font-normal text-[#3c4043]">
          {(item as ExistingRateGroupActivity & { unitName?: string | null }).unitName || "-"}
        </span>
      </div>

      </div>

      
    </div>

    {/* EXPANDED CARD (Section Stage Items) */}
    {expandedTradeItems.has(item.rateGroupNo) && (() => {
      const filteredSubWorks = (subWorks[item.rateGroupNo] || []).filter(
        (subWork) => Number(subWork.workGroup) !== Number(item.stage)
      );
      const filteredSubItems = (subItems[item.rateGroupNo] || []).filter(
        (subItem) => Number(subItem.procedure) !== Number(item.rider)
      );
      const itemSpecs = specsByRateGroup[item.rateGroupNo] || [];
      const parentRateType = "Stage Item" as const;
      const actionButtonClass = "rounded-md px-2 py-1 text-[13px] text-[#53538c] hover:bg-[#e8f0fe] hover:text-[#1a73e8] disabled:cursor-not-allowed disabled:opacity-60";

      return (
        <div className="ml-[3.5rem] mb-2 mt-1 rounded-md border border-gray-100 bg-[#fafbfc] px-2 py-2">
          {/* Workgroups and Add Sub Works stay on the same row */}
          <div className="flex flex-wrap items-center gap-1.5">
            {filteredSubWorks.map((subWork) => (
              <div key={subWork.rateWorkGroupNo} className="group inline-flex items-center rounded-full border border-[#FDE68A] bg-[#FEFCE8] px-2 py-1 text-[13px] text-[#854D0E]">
                <span className="mr-1" title="Sub work">🛠️</span>
                <span>{subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}</span>
                <button
                  type="button"
                  title="Delete Sub Work"
                  aria-label={`Delete ${subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingTagDelete({
                      kind: "subWork",
                      rateGroupNo: Number(item.rateGroupNo),
                      recordNo: Number(subWork.rateWorkGroupNo),
                      label: subWork.workGroupName || `WorkGroup ${subWork.workGroup}`,
                    });
                  }}
                  className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-[#92400E] opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                >
                  ×
                </button>
              </div>
            ))}
            {filteredSubWorks.length === 0 && (
              <span className="text-[13px] text-gray-500">No workgroups are added.</span>
            )}
            <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubWorks?.(item.rateGroupNo, parentRateType); }} className={actionButtonClass}>
              🛠️ Add Sub Works
            </button>
          </div>

          {/* Sub-items and Add Sub Item stay on the same row */}
          {filteredSubWorks.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {filteredSubItems.map((subItem) => (
                <div key={subItem.rateItemNo} className="group inline-flex items-center rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2 py-1 text-[13px] text-[#1D4ED8]">
                  <span className="mr-1" title="Sub item activity">📋</span>
                  <span>{subItem.activityName || `Activity ${subItem.procedure}`}</span>
                  <button
                    type="button"
                    title="Delete Sub Item"
                    aria-label={`Delete ${subItem.activityName || `Activity ${subItem.procedure}`}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "subItem",
                        rateGroupNo: Number(item.rateGroupNo),
                        recordNo: Number(subItem.rateItemNo),
                        label: subItem.activityName || `Activity ${subItem.procedure}`,
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-blue-700 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {filteredSubItems.length === 0 && (
                <span className="text-[13px] text-gray-500">No sub items are added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubItem?.(item.rateGroupNo, item.wbsNo ?? null, parentRateType); }} className={actionButtonClass}>
                + Add Sub Item
              </button>
            </div>
          )}

          {/* Specifications are shown as items only; no title or empty-state message */}
          {filteredSubWorks.length > 0 && filteredSubItems.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {itemSpecs.map((spec, index) => (
                <div key={`${spec.type}-${spec.id}-${index}`} className="group inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[13px] text-gray-700">
                  <span>{spec.name || "Unnamed specification"}</span>
                  <button
                    type="button"
                    title="Delete Specification"
                    aria-label={`Delete ${spec.name || "Unnamed specification"}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "spec",
                        rateGroupNo: Number(item.rateGroupNo),
                        component: Number(spec.component),
                        selectedItemID: Number(spec.id),
                        label: spec.name || "Unnamed specification",
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-gray-500 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {itemSpecs.length === 0 && (
                <span className="text-[13px] text-gray-500">No specifications added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); void handleAddSpecs(item); }} disabled={!!loadingSpecs[item.rateGroupNo]} className={actionButtonClass}>
                {loadingSpecs[item.rateGroupNo] ? "Loading..." : "+ Add Specs"}
              </button>
            </div>
          )}
        </div>
      );
    })()}

    </React.Fragment>
))}


                        </div>
                      </>
                    )}

                    </React.Fragment>

                  );

                }

              )}

            </div>

          )}

        {/* DIRECT TRADE ITEMS */}
        {!loading &&

          directTradeItems.length > 0 && (

            <div className="mb-3 flex flex-col gap-[2px]">


              {directTradeItems.map((item) => (

  <React.Fragment key={item.rateGroupNo}>

    <div
      className="group relative flex min-h-[38px] w-full items-center rounded-lg border border-transparent bg-white px-3 hover:border-gray-200 hover:bg-[#f1f3f4]"
      style={{ paddingLeft: `${intentLevel * 16}px` }}
    >

      <button type="button" onClick={(event) => { event.stopPropagation(); toggleTradeItem(item.rateGroupNo); }} className="mr-1 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded hover:bg-gray-100" title={expandedTradeItems.has(item.rateGroupNo) ? "Collapse Sub Items" : "Expand Sub Items"}>

        <span className="text-[13px] text-gray-500">{expandedTradeItems.has(item.rateGroupNo) ? "▼" : "▶"}</span>

      </button>

      <span className="mr-2 flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-md bg-[#e8f0fe] text-[#2563eb]" title="Trade Item"><HardHat size={15} strokeWidth={1.8} /></span>

      {editingRateGroupNo === item.rateGroupNo && editingRateField === "name" ? (
        <input
          autoFocus
          value={editRateGroupName}
          onChange={(event) => setEditRateGroupName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="w-fit min-w-[4ch] max-w-[260px] rounded border border-gray-300 px-1.5 py-1 text-[13px]"
          style={{ width: `${Math.min(32, Math.max(4, editRateGroupName.length + 1))}ch` }}
          aria-label="Rate Group name"
        />
      ) : (
        <button
          type="button"
          title="Click to edit name"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("name");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="inline-block w-fit max-w-[260px] truncate text-left text-[13px] font-normal text-[#202124] no-underline"
        >{item.rateGroup || "-"}</button>
      )}

      <button
            type="button"
            title="Delete Rate Group"
            aria-label={`Delete ${item.rateGroup || "Rate Group"}`}
            onClick={(event) => {
              event.stopPropagation();
              setPendingTagDelete({
                kind: "rateGroup",
                rateGroupNo: Number(item.rateGroupNo),
                label: item.rateGroup || "Rate Group",
              });
            }}
            className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
          >
            ×
          </button>

          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-3">
      {editingRateGroupNo === item.rateGroupNo && editingRateField === "rate" ? (
        <input
          autoFocus
          type="number"
          min="0"
          step="0.01"
          value={editRate}
          onChange={(event) => setEditRate(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="h-[27px] w-24 shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-center text-[13px]"
          aria-label="Rate"
        />
      ) : (
        <button
          type="button"
          title="Click to edit rate"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("rate");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="shrink-0 whitespace-nowrap text-[13px] font-normal text-[#53538c] no-underline"
        >Rate: {item.rate == null ? "0.00" : Number(item.rate).toFixed(2)}</button>
      )}

      {/* UNIT SELECTOR */}
      <div className="ml-3 flex shrink-0 items-center gap-1.5">
        <label
          htmlFor={`trade-item-unit-${item.rateGroupNo}`}
          className="whitespace-nowrap text-[13px] font-normal text-[#5f6368]"
        >
          Unit:
        </label>
        <select
          id={`trade-item-unit-${item.rateGroupNo}`}
          value={selectedUnitByRateGroup[item.rateGroupNo] ?? (item.unit == null ? "" : String(item.unit))}
          onChange={async (event) => {
            event.stopPropagation();

            const previousValue =
              selectedUnitByRateGroup[item.rateGroupNo] ??
              (item.unit == null ? "" : String(item.unit));
            const selectedValue = event.target.value;
            const unit = selectedValue === "" ? null : Number(selectedValue);

            if (unit !== null && !Number.isInteger(unit)) return;

            setSelectedUnitByRateGroup((previous) => ({
              ...previous,
              [item.rateGroupNo]: selectedValue,
            }));

            try {
              await RateGroupAPI.updateRateGroupUnit({
                rateGroupNo: item.rateGroupNo,
                unit,
              });
            } catch (error) {
              console.error("Unable to save Trade Item unit:", error);
              setSelectedUnitByRateGroup((previous) => ({
                ...previous,
                [item.rateGroupNo]: previousValue,
              }));
              alert("Unable to save the selected unit. Please try again.");
            }
          }}
          onClick={(event) => event.stopPropagation()}
          disabled={!!loadingUnitsByRateGroup[item.rateGroupNo] || (unitsByRateGroup[item.rateGroupNo] ?? []).length === 0}
          aria-label={`Unit for ${item.rateGroup || "Trade Item"}`}
          title="Select unit"
          className="h-[27px] w-[115px] shrink-0 rounded border border-gray-200 bg-white px-2 text-[13px] font-normal text-[#3c4043] outline-none focus:border-[#9bbcf0] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">
            {loadingUnitsByRateGroup[item.rateGroupNo]
              ? "Loading units..."
              : (unitsByRateGroup[item.rateGroupNo] ?? []).length > 0
                ? "Select unit"
                : "No units"}
          </option>
          {(unitsByRateGroup[item.rateGroupNo] ?? []).map((unit) => (
            <option key={unit.unitNo} value={String(unit.unitNo)}>
              {unit.unitName}
            </option>
          ))}
        </select>
      </div>

      </div>

      

    </div>

        {/* EXPANDED CARD (Direct Trade Items) */}
    {expandedTradeItems.has(item.rateGroupNo) && (() => {
      const filteredSubWorks = (subWorks[item.rateGroupNo] || []).filter(
        (subWork) => Number(subWork.workGroup) !== Number(item.stage)
      );
      const filteredSubItems = (subItems[item.rateGroupNo] || []).filter(
        (subItem) => Number(subItem.procedure) !== Number(item.rider)
      );
      const itemSpecs = specsByRateGroup[item.rateGroupNo] || [];
      const parentRateType = "Trade Item" as const;
      const actionButtonClass = "rounded-md px-2 py-1 text-[13px] text-[#53538c] hover:bg-[#e8f0fe] hover:text-[#1a73e8] disabled:cursor-not-allowed disabled:opacity-60";

      return (
        <div className="ml-[3.5rem] mb-2 mt-1 rounded-md border border-gray-100 bg-[#fafbfc] px-2 py-2">
          {/* Workgroups and Add Sub Works stay on the same row */}
          <div className="flex flex-wrap items-center gap-1.5">
            {filteredSubWorks.map((subWork) => (
              <div key={subWork.rateWorkGroupNo} className="group inline-flex items-center rounded-full border border-[#FDE68A] bg-[#FEFCE8] px-2 py-1 text-[13px] text-[#854D0E]">
                <span className="mr-1" title="Sub work">🛠️</span>
                <span>{subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}</span>
                <button
                  type="button"
                  title="Delete Sub Work"
                  aria-label={`Delete ${subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingTagDelete({
                      kind: "subWork",
                      rateGroupNo: Number(item.rateGroupNo),
                      recordNo: Number(subWork.rateWorkGroupNo),
                      label: subWork.workGroupName || `WorkGroup ${subWork.workGroup}`,
                    });
                  }}
                  className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-[#92400E] opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                >
                  ×
                </button>
              </div>
            ))}
            {filteredSubWorks.length === 0 && (
              <span className="text-[13px] text-gray-500">No workgroups are added.</span>
            )}
            <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubWorks?.(item.rateGroupNo, parentRateType); }} className={actionButtonClass}>
              🛠️ Add Sub Works
            </button>
          </div>

          {/* Sub-items and Add Sub Item stay on the same row */}
          {filteredSubWorks.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {filteredSubItems.map((subItem) => (
                <div key={subItem.rateItemNo} className="group inline-flex items-center rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2 py-1 text-[13px] text-[#1D4ED8]">
                  <span className="mr-1" title="Sub item activity">📋</span>
                  <span>{subItem.activityName || `Activity ${subItem.procedure}`}</span>
                  <button
                    type="button"
                    title="Delete Sub Item"
                    aria-label={`Delete ${subItem.activityName || `Activity ${subItem.procedure}`}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "subItem",
                        rateGroupNo: Number(item.rateGroupNo),
                        recordNo: Number(subItem.rateItemNo),
                        label: subItem.activityName || `Activity ${subItem.procedure}`,
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-blue-700 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {filteredSubItems.length === 0 && (
                <span className="text-[13px] text-gray-500">No sub items are added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubItem?.(item.rateGroupNo, item.wbsNo ?? null, parentRateType); }} className={actionButtonClass}>
                + Add Sub Item
              </button>
            </div>
          )}

          {/* Specifications are shown as items only; no title or empty-state message */}
          {filteredSubWorks.length > 0 && filteredSubItems.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {itemSpecs.map((spec, index) => (
                <div key={`${spec.type}-${spec.id}-${index}`} className="group inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[13px] text-gray-700">
                  <span>{spec.name || "Unnamed specification"}</span>
                  <button
                    type="button"
                    title="Delete Specification"
                    aria-label={`Delete ${spec.name || "Unnamed specification"}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "spec",
                        rateGroupNo: Number(item.rateGroupNo),
                        component: Number(spec.component),
                        selectedItemID: Number(spec.id),
                        label: spec.name || "Unnamed specification",
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-gray-500 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {itemSpecs.length === 0 && (
                <span className="text-[13px] text-gray-500">No specifications added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); void handleAddSpecs(item); }} disabled={!!loadingSpecs[item.rateGroupNo]} className={actionButtonClass}>
                {loadingSpecs[item.rateGroupNo] ? "Loading..." : "+ Add Specs"}
              </button>
            </div>
          )}
        </div>
      );
    })()}

    </React.Fragment>

))}

            </div>

          )}

{/* DIRECT STAGE ITEMS */}
        {!loading && directStageItems.length > 0 && (
          <div className="mb-3 flex flex-col gap-[2px]">

{directStageItems.map((item) => (
  <React.Fragment key={item.rateGroupNo}>
    {/* DIRECT STAGE ITEM ROW */}
    <div
      className="group relative flex min-h-[38px] w-full items-center rounded-lg border border-transparent bg-white px-3 hover:border-gray-200 hover:bg-[#f1f3f4]"
      style={{ paddingLeft: `${intentLevel * 16}px` }}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          void toggleTradeItem(item.rateGroupNo);
        }}
        className="mr-1 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded hover:bg-gray-100"
        title={
          expandedTradeItems.has(item.rateGroupNo)
            ? "Collapse"
            : "Expand"
        }
      >
        <span className="text-[13px] text-gray-500">
          {expandedTradeItems.has(item.rateGroupNo) ? "▼" : "▶"}
        </span>
      </button>

      <span className="mr-2 flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-md bg-[#f3e8ff] text-[#7c3aed]" title="Stage Item">
        <Construction size={15} strokeWidth={1.8} />
      </span>

      {editingRateGroupNo === item.rateGroupNo && editingRateField === "name" ? (
        <input
          autoFocus
          value={editRateGroupName}
          onChange={(event) => setEditRateGroupName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="w-fit min-w-[4ch] max-w-[260px] rounded border border-gray-300 px-1.5 py-1 text-[13px]"
          style={{ width: `${Math.min(32, Math.max(4, editRateGroupName.length + 1))}ch` }}
          aria-label="Rate Group name"
        />
      ) : (
        <button
          type="button"
          title="Click to edit name"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("name");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="inline-block w-fit max-w-[260px] truncate text-left text-[13px] font-normal text-[#202124] no-underline"
        >{item.rateGroup || "-"}</button>
      )}

      <button
            type="button"
            title="Delete Rate Group"
            aria-label={`Delete ${item.rateGroup || "Rate Group"}`}
            onClick={(event) => {
              event.stopPropagation();
              setPendingTagDelete({
                kind: "rateGroup",
                rateGroupNo: Number(item.rateGroupNo),
                label: item.rateGroup || "Rate Group",
              });
            }}
            className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
          >
            ×
          </button>

          <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-3">
      {editingRateGroupNo === item.rateGroupNo && editingRateField === "rate" ? (
        <input
          autoFocus
          type="number"
          min="0"
          step="0.01"
          value={editRate}
          onChange={(event) => setEditRate(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") { event.preventDefault(); void handleSaveRateGroup(item); }
            if (event.key === "Escape") cancelRateGroupEdit();
          }}
          className="h-[27px] w-24 shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-center text-[13px]"
          aria-label="Rate"
        />
      ) : (
        <button
          type="button"
          title="Click to edit rate"
          onClick={(event) => {
            event.stopPropagation();
            setEditingRateGroupNo(item.rateGroupNo);
            setEditingRateField("rate");
            setEditRateGroupName(item.rateGroup ?? "");
            setEditRate(String(item.rate ?? 0));
          }}
          className="shrink-0 whitespace-nowrap text-[13px] font-normal text-[#53538c] no-underline"
        >Rate: {item.rate == null ? "0.00" : Number(item.rate).toFixed(2)}</button>
      )}

      {/* Unit name */}
      <div className="ml-3 flex shrink-0 items-center gap-1.5" aria-label="Stage Item unit">
        <span className="whitespace-nowrap text-[13px] font-normal text-[#5f6368]">
          Unit:
        </span>
        <span className="inline-flex h-[27px] min-w-[115px] items-center rounded border border-gray-200 bg-gray-50 px-2 text-[13px] font-normal text-[#3c4043]">
          {(item as ExistingRateGroupActivity & { unitName?: string | null }).unitName || "-"}
        </span>
      </div>

      </div>

      
    </div>

    {/* EXPANDED CARD (Direct Stage Items) */}
    {expandedTradeItems.has(item.rateGroupNo) && (() => {
      const filteredSubWorks = (subWorks[item.rateGroupNo] || []).filter(
        (subWork) => Number(subWork.workGroup) !== Number(item.stage)
      );
      const filteredSubItems = (subItems[item.rateGroupNo] || []).filter(
        (subItem) => Number(subItem.procedure) !== Number(item.rider)
      );
      const itemSpecs = specsByRateGroup[item.rateGroupNo] || [];
      const parentRateType = "Stage Item" as const;
      const actionButtonClass = "rounded-md px-2 py-1 text-[13px] text-[#53538c] hover:bg-[#e8f0fe] hover:text-[#1a73e8] disabled:cursor-not-allowed disabled:opacity-60";

      return (
        <div className="ml-[3.5rem] mb-2 mt-1 rounded-md border border-gray-100 bg-[#fafbfc] px-2 py-2">
          {/* Workgroups and Add Sub Works stay on the same row */}
          <div className="flex flex-wrap items-center gap-1.5">
            {filteredSubWorks.map((subWork) => (
              <div key={subWork.rateWorkGroupNo} className="group inline-flex items-center rounded-full border border-[#FDE68A] bg-[#FEFCE8] px-2 py-1 text-[13px] text-[#854D0E]">
                <span className="mr-1" title="Sub work">🛠️</span>
                <span>{subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}</span>
                <button
                  type="button"
                  title="Delete Sub Work"
                  aria-label={`Delete ${subWork.workGroupName || `WorkGroup ${subWork.workGroup}`}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingTagDelete({
                      kind: "subWork",
                      rateGroupNo: Number(item.rateGroupNo),
                      recordNo: Number(subWork.rateWorkGroupNo),
                      label: subWork.workGroupName || `WorkGroup ${subWork.workGroup}`,
                    });
                  }}
                  className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-[#92400E] opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                >
                  ×
                </button>
              </div>
            ))}
            {filteredSubWorks.length === 0 && (
              <span className="text-[13px] text-gray-500">No workgroups are added.</span>
            )}
            <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubWorks?.(item.rateGroupNo, parentRateType); }} className={actionButtonClass}>
              🛠️ Add Sub Works
            </button>
          </div>

          {/* Sub-items and Add Sub Item stay on the same row */}
          {filteredSubWorks.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {filteredSubItems.map((subItem) => (
                <div key={subItem.rateItemNo} className="group inline-flex items-center rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2 py-1 text-[13px] text-[#1D4ED8]">
                  <span className="mr-1" title="Sub item activity">📋</span>
                  <span>{subItem.activityName || `Activity ${subItem.procedure}`}</span>
                  <button
                    type="button"
                    title="Delete Sub Item"
                    aria-label={`Delete ${subItem.activityName || `Activity ${subItem.procedure}`}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "subItem",
                        rateGroupNo: Number(item.rateGroupNo),
                        recordNo: Number(subItem.rateItemNo),
                        label: subItem.activityName || `Activity ${subItem.procedure}`,
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-blue-700 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {filteredSubItems.length === 0 && (
                <span className="text-[13px] text-gray-500">No sub items are added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); onAddSubItem?.(item.rateGroupNo, item.wbsNo ?? null, parentRateType); }} className={actionButtonClass}>
                + Add Sub Item
              </button>
            </div>
          )}

          {/* Specifications are shown as items only; no title or empty-state message */}
          {filteredSubWorks.length > 0 && filteredSubItems.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {itemSpecs.map((spec, index) => (
                <div key={`${spec.type}-${spec.id}-${index}`} className="group inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[13px] text-gray-700">
                  <span>{spec.name || "Unnamed specification"}</span>
                  <button
                    type="button"
                    title="Delete Specification"
                    aria-label={`Delete ${spec.name || "Unnamed specification"}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingTagDelete({
                        kind: "spec",
                        rateGroupNo: Number(item.rateGroupNo),
                        component: Number(spec.component),
                        selectedItemID: Number(spec.id),
                        label: spec.name || "Unnamed specification",
                      });
                    }}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full text-gray-500 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              {itemSpecs.length === 0 && (
                <span className="text-[13px] text-gray-500">No specifications added.</span>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); void handleAddSpecs(item); }} disabled={!!loadingSpecs[item.rateGroupNo]} className={actionButtonClass}>
                {loadingSpecs[item.rateGroupNo] ? "Loading..." : "+ Add Specs"}
              </button>
            </div>
          )}
        </div>
      );
    })()}

    </React.Fragment>
))}


          </div>
        )}


      </div>

      {/* SHARED DELETE CONFIRMATION POPUP: TAGS AND CONTRACT SECTIONS */}
      {(pendingTagDelete || pendingSectionDelete) && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            if (!deletingTag && !deletingSection) {
              setPendingTagDelete(null);
              setPendingSectionDelete(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-tag-popup-title"
            className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
            style={{ fontFamily: '"Segoe UI", sans-serif', fontSize: "13px" }}
          >
            <h3
              id="delete-tag-popup-title"
              className="mb-3 text-[15px] font-semibold text-[#53538c]"
            >
              {pendingSectionDelete ? "Confirm Section Delete" : "Confirm Delete"}
            </h3>

            <p className="mb-3 text-[13px] text-gray-600">
              Are you sure you want to delete{" "}
              <strong className="text-gray-800">
                {pendingSectionDelete
                  ? pendingSectionDelete.contractNode
                  : pendingTagDelete?.label}
              </strong>
              ?
            </p>

            {pendingSectionDelete && (
              <p className="mb-5 text-[12px] text-red-600">
                All child sections under this section will also be deleted.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={deletingTag || deletingSection}
                onClick={() => {
                  setPendingTagDelete(null);
                  setPendingSectionDelete(null);
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={deletingTag || deletingSection}
                onClick={() => {
                  if (pendingSectionDelete) {
                    void confirmSectionDelete();
                  } else {
                    void confirmTagDelete();
                  }
                }}
                className="rounded-md bg-red-600 px-4 py-2 text-[13px] text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deletingSection || deletingTag ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD SPECS POPUP */}
      {showSpecsPopup && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            setShowSpecsPopup(false);
            setActiveSpecsRateGroupNo(null);
            setSelectedSpec("");
            setSelectedRelatedId("");
            setRelatedItems([]);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-specs-popup-title"
            className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
            style={{ fontFamily: '"Segoe UI", sans-serif', fontSize: "13px" }}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3
                id="add-specs-popup-title"
                className="text-[14px] font-semibold text-[#53538c]"
              >
                Select Specification
              </h3>
              <button
                type="button"
                aria-label="Close specification popup"
                onClick={() => {
                  setShowSpecsPopup(false);
                  setActiveSpecsRateGroupNo(null);
                  setSelectedSpec("");
                  setSelectedRelatedId("");
                  setRelatedItems([]);
                }}
                className="flex h-7 w-7 items-center justify-center rounded text-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="add-specs-select"
                  className="mb-1.5 block text-[13px] text-gray-600"
                >
                  Specs by Activity
                </label>
                <select
                  id="add-specs-select"
                  value={selectedSpec}
                  onChange={(event) => {
                    void handleSpecChange(event.target.value);
                  }}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-800 outline-none focus:border-[#9bbcf0] focus:ring-1 focus:ring-[#9bbcf0]"
                >
                  <option value="">Select specification</option>
                  {availableSpecs.map((spec, index) => (
                    <option
                      key={`${spec.type}-${spec.id}-${index}`}
                      value={String(index)}
                    >
                      {spec.name || "Unnamed specification"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="related-items-select"
                  className="mb-1.5 block text-[13px] text-gray-600"
                >
                  {selectedSpec !== "" &&
                  Number(availableSpecs[Number(selectedSpec)]?.id) < 0
                    ? "Procedures"
                    : "Components"}
                </label>
                <select
                  id="related-items-select"
                  value={selectedRelatedId}
                  onChange={(event) =>
                    setSelectedRelatedId(event.target.value)
                  }
                  disabled={selectedSpec === "" || loadingRelated}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-800 outline-none focus:border-[#9bbcf0] focus:ring-1 focus:ring-[#9bbcf0] disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  <option value="">
                    {loadingRelated
                      ? "Loading..."
                      : selectedSpec === ""
                        ? "Select specification first"
                        : "Select item"}
                  </option>
                  {relatedItems.map((item, index) => (
                    <option
                      key={`${item.id}-${index}`}
                      value={String(item.id)}
                    >
                      {item.name}
                    </option>
                  ))}
                </select>
                {!loadingRelated &&
                  selectedSpec !== "" &&
                  relatedItems.length === 0 && (
                    <p className="mt-1 text-[12px] text-gray-500">
                      No related items found.
                    </p>
                  )}
              </div>
            </div>

            {availableSpecs.length === 0 && (
              <p className="mt-2 text-[13px] text-gray-500">
                No specifications found for this Trade Item.
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowSpecsPopup(false);
                  setActiveSpecsRateGroupNo(null);
                  setSelectedSpec("");
                  setSelectedRelatedId("");
                  setRelatedItems([]);
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={selectedSpec === "" || selectedRelatedId === "" || loadingRelated}
                onClick={handleConfirmSpec}
                className="rounded-md bg-[#53538c] px-4 py-2 text-[13px] text-white hover:bg-[#454575] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

    </div>

  );

};

export default ContractSectionTabs;